import React from 'react'
import * as yup from 'yup'
import * as Request from '../../utilities/request'
import * as PermissionID from '../../constants/permission'
import { Grade, PermissionReturn, Staff, StaffGradeUpdate, StaffStandardWorkingHours } from '../../../../back-end/utilities/apiDefinitions'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { Formik, FormikErrors } from 'formik'
import { FormText } from '../ui/Form/Text'
import { FormNumber } from '../ui/Form/Number'

import './editStaffModal.css'
import { DateTime } from 'luxon'
import { FormDate } from '../ui/Form/Date'
import { AuthState } from '../../App.d'
import { formatDateExcludeEpoch } from '../../utilities/format'
import { FormSelect, SelectOption } from '../ui/Form/Select'
import { Search } from 'react-feather'

const defaultStaffGradeUpdate: StaffGradeUpdate = {
	gradeID: '',
	effectiveDate: DateTime.now().plus({ days: 1 }).toISODate(),
}

const defaultStaffStandardWorkingHours: Partial<StaffStandardWorkingHours> = {
	hours: 0,
	effectiveDate: DateTime.now().plus({ days: 1 }).toISODate(),
}

type FormStaff = Staff & {
	inviteUser: boolean
	initGradeHistory: StaffGradeUpdate
	initStandardWorkingHours: Partial<StaffStandardWorkingHours>
	sendInvite: boolean
}

interface EditStaffModalProps {
	authState: AuthState
	modalStaff: Staff | null
	setModalStaff: React.Dispatch<React.SetStateAction<Staff | null>>
	setOffboardStaff: React.Dispatch<React.SetStateAction<Staff | null>>
	allStaff: Staff[]
	setAllStaff: React.Dispatch<React.SetStateAction<Staff[]>>
	permissions: PermissionReturn[]
	grades: Grade[]
}

const EditStaffModal = (props: EditStaffModalProps) => {
	const [addingStaffGrade, setAddingStaffGrade] = React.useState(false)
	const [newStaffGrade, setNewStaffGrade] = React.useState<StaffGradeUpdate>(defaultStaffGradeUpdate)
	const [addingStaffHours, setAddingStaffHours] = React.useState(false)
	const [newStaffHours, setNewStaffHours] = React.useState<Partial<StaffStandardWorkingHours>>(defaultStaffStandardWorkingHours)

	if (props.modalStaff === null) {
		return null
	}

	const isFirstEdit = !props.modalStaff.staffGradeHistory || props.modalStaff.staffGradeHistory.length === 0

	const onHide = (isSubmitting: boolean) => {
		if (!isSubmitting) {
			props.setModalStaff(null)
		}
	}

	const onSubmit = async (values: FormStaff) => {
		if (isFirstEdit) {
			const gradeBody: StaffGradeUpdate = {
				gradeID: values.initGradeHistory?.gradeID || '',
			}
			const standardWorkingHoursBody: Partial<StaffStandardWorkingHours> = {
				hours: values.initStandardWorkingHours?.hours || 0,
			}

			await Promise.all([
				Request.put(`staff/grade?where=StaffID==${values.staffID}`, gradeBody),
				Request.put(`staff/swh?where=StaffID==${values.staffID}`, standardWorkingHoursBody),
			])
		}

		const updateStaffRes = await Request.put(`staff${values.sendInvite ? '/invite' : ''}?where=StaffID==${values.staffID}`, values)
		if (updateStaffRes.data.success) {
			props.setAllStaff(props.allStaff.map((staff) => (staff.staffID === values.staffID ? updateStaffRes.data.staff[0] : staff)))
			props.setModalStaff(null)
		}
	}

	const onSubmitNewStaffGrade = async (
		setSubmitting: (isSubmitting: boolean) => void,
		setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Staff>>
	) => {
		if (props.modalStaff === null) {
			return
		}
		setSubmitting(true)

		const newStaffRes = await Request.put(`staff/grade?where=StaffID==${props.modalStaff.staffID}`, newStaffGrade)

		if (newStaffRes.data.success) {
			setFieldValue('staffGradeHistory', newStaffRes.data.staff[0].staffGradeHistory)
			props.setAllStaff(props.allStaff.map((s) => (s.staffID === props.modalStaff?.staffID ? newStaffRes.data.staff[0] : s)))
			setNewStaffGrade(defaultStaffGradeUpdate)
			setAddingStaffGrade(false)
		}

		setSubmitting(false)
	}

	const onSubmitNewStaffHours = async (
		setSubmitting: (isSubmitting: boolean) => void,
		setFieldValue: (field: string, value: unknown, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<Staff>>
	) => {
		if (props.modalStaff === null) {
			return
		}
		setSubmitting(true)

		const newStaffRes = await Request.put(`staff/swh?where=StaffID==${props.modalStaff.staffID}`, newStaffHours)

		if (newStaffRes.data.success) {
			setFieldValue('standardWorkingHoursHistory', newStaffRes.data.staff[0].standardWorkingHoursHistory)
			props.setAllStaff(props.allStaff.map((s) => (s.staffID === props.modalStaff?.staffID ? newStaffRes.data.staff[0] : s)))
			setNewStaffHours(defaultStaffStandardWorkingHours)
			setAddingStaffHours(false)
		}

		setSubmitting(false)
	}

	const reInviteUser = async () => {
		if (props.modalStaff?.userID === null) {
			return
		}

		await Request.put(`user/${props.modalStaff?.userID}/invite/billy`, { resend: true })
	}

	const labelCol = 5
	const labelJustify = 'end'

	const padCol = !isFirstEdit ? <Col sm={3} /> : null

	const StaffValidationSchema = yup.object().shape({
		reportGroupStaffXpmID: yup.string().nullable().required('Reporting Group is required'),
		initGradeHistory: yup.object().shape({
			gradeID: isFirstEdit ? yup.string().required('Grade is required') : yup.string().optional(),
		}),
	})

	return (
		<Formik
			initialValues={{
				...props.modalStaff,
				inviteUser: false,
				initGradeHistory: defaultStaffGradeUpdate,
				initStandardWorkingHours: defaultStaffStandardWorkingHours,
				sendInvite: false,
			}}
			validationSchema={StaffValidationSchema}
			onSubmit={onSubmit}
		>
			{({ values, errors, touched, handleBlur, handleSubmit, isSubmitting, setFieldValue, setSubmitting, setValues }) => (
				<Modal show={!!props.modalStaff} onHide={() => onHide(isSubmitting)} dialogClassName={`edit-staff-modal${isFirstEdit ? '' : '-lg'}`}>
					<Modal.Header>
						<Row className="align-items-center justify-content-between w-100">
							<Col sm="auto">
								<h4 className="m-0">{isFirstEdit ? 'Setup Staff' : 'Edit Staff'}</h4>
							</Col>
							<Col sm="auto" className="p-0">
								{!isFirstEdit && values.user !== null && !values.user.invitationAccepted && (
									<Button
										variant="secondary"
										className="p-2"
										disabled={isSubmitting || addingStaffGrade || addingStaffHours}
										onClick={() => reInviteUser()}
									>
										Resend Invite
									</Button>
								)}
							</Col>
						</Row>
					</Modal.Header>
					<Modal.Body className="staff-modal-body">
						<Row>
							{padCol}
							<Col className="staff-modal-body">
								<FormText
									name="name"
									label="Staff Member Name"
									labelCol={labelCol}
									labelJustify={labelJustify}
									disabled
									readOnly
									value={`${values.name} (${values.email})`}
								/>
								<FormSelect
									name="permissionID"
									label="Permission"
									labelCol={labelCol}
									labelJustify={labelJustify}
									onChange={(e) => {
										const value = e ? (e as SelectOption<string>).value : ''
										if (value === PermissionID.Director) {
											setValues((values) => ({ ...values, permissionID: value, reportGroupStaffXpmID: values.xpmID }))
										} else {
											setValues((values) => ({ ...values, permissionID: value, reportGroupStaffXpmID: '' }))
										}
									}}
									// TODO: Limit options based on permission hierarchy?
									options={props.permissions.sort((a, b) => (a.name < b.name ? -1 : 1)).map((p) => ({ value: p.id, label: p.name }))}
									value={values.permissionID || ''}
									isSearchable={false}
									isClearable
								/>

								<FormSelect
									name="reportGroupStaffXpmID"
									label="Reporting Group"
									labelCol={labelCol}
									labelJustify={labelJustify}
									placeholder={
										<div className="d-flex align-items-center">
											<Search size={14} />
											<span className="pl-2">Search or select a director</span>
										</div>
									}
									options={props.allStaff
										.filter((s) => s.permissionID === PermissionID.Director)
										.sort((a, b) => (a.name < b.name ? -1 : 1))
										.map((s) => ({ value: s.xpmID, label: s.name }))}
									value={values.reportGroupStaffXpmID}
									onChange={(e) => setFieldValue('reportGroupStaffXpmID', e ? (e as SelectOption<string>).value : '')}
									onBlur={handleBlur}
									required
									feedback={touched.reportGroupStaffXpmID ? errors.reportGroupStaffXpmID : ''}
									isDisabled={values.permissionID === PermissionID.Director}
								/>
								{values.terminationDate ? (
									<Row>
										{' '}
										<Col className="staff-modal-body">
											<FormDate
												label="Last Day"
												name="terminationDate"
												labelCol={labelCol}
												labelJustify={labelJustify}
												value={values.terminationDate ? DateTime.fromISO(values.terminationDate).toISODate() : null}
												readOnly
												disabled
												plaintext
											/>
										</Col>
									</Row>
								) : null}

								{values.permissionID === PermissionID.Director && (
									<div className="center-flex">
										<span>Note: Directors are automatically assigned to their own reporting group.</span>
									</div>
								)}
							</Col>
							{padCol}
						</Row>
						{isFirstEdit ? (
							<Row>
								{padCol}
								<Col className="staff-modal-body">
									<FormSelect
										name="initGradeHistory.gradeID"
										label="Grade"
										labelCol={labelCol}
										labelJustify={labelJustify}
										options={props.grades.map((p) => ({ value: p.gradeID, label: p.name }))}
										value={values.initGradeHistory.gradeID}
										onChange={(e) => setFieldValue('initGradeHistory.gradeID', e ? (e as SelectOption<string>).value : '')}
										onBlur={handleBlur}
										isSearchable={false}
										feedback={touched.initGradeHistory?.gradeID ? errors.initGradeHistory?.gradeID : ''}
										required
									/>
									<FormNumber
										name="initStandardWorkingHours.hours"
										label="Standard Working Hours"
										labelCol={labelCol}
										labelJustify={labelJustify}
										value={values.initStandardWorkingHours.hours || 0}
										onChange={(value) => setFieldValue('initStandardWorkingHours.hours', value)}
										onBlur={handleBlur}
										onFocus={(e) => e.target.select()}
										min={0}
										max={6000}
										feedback={touched.initStandardWorkingHours?.hours ? errors.initStandardWorkingHours?.hours : ''}
										required
									/>
								</Col>
								{padCol}
							</Row>
						) : (
							<Row className="px-3">
								<Col>
									<Row>
										<Col sm="auto">
											<Form.Label>Grade</Form.Label>
										</Col>
									</Row>
									<Row className="mini-table-header align-items-center py-2">
										<Col sm={4}>Name</Col>
										<Col sm={4}>Effective Date</Col>
										<Col sm={4} className="center-flex">
											<Button
												variant="secondary"
												className="px-2 py-1"
												disabled={isSubmitting || addingStaffGrade || addingStaffHours}
												onClick={() => {
													setNewStaffGrade(defaultStaffGradeUpdate)
													setAddingStaffGrade(true)
												}}
											>
												Add
											</Button>
										</Col>
									</Row>
									{addingStaffGrade && (
										<Row className="align-items-center mt-3">
											<Col sm={4}>
												<FormSelect
													name="newStaffGradeGradeID"
													onChange={(e) =>
														setNewStaffGrade({ ...newStaffGrade, gradeID: e ? (e as SelectOption<string>).value : '' })
													}
													options={props.grades.map((p) => ({ value: p.gradeID, label: p.name }))}
													value={newStaffGrade.gradeID}
													required
												/>
											</Col>
											<Col sm={4}>
												<FormDate
													name="newStaffGradeEffectiveDate"
													value={newStaffGrade.effectiveDate}
													onChange={(e) => setNewStaffGrade({ ...newStaffGrade, effectiveDate: e.target.value })}
													min={DateTime.now().plus({ days: 1 }).toISODate()}
												/>
											</Col>
											<Col sm={2} className="center-flex">
												<Button
													variant="secondary"
													className="px-2 py-1"
													disabled={isSubmitting}
													onClick={() => setAddingStaffGrade(false)}
												>
													Cancel
												</Button>
											</Col>
											<Col sm={2} className="center-flex">
												<Button
													variant="primary"
													className="px-2 py-1"
													disabled={isSubmitting || newStaffGrade.gradeID === ''}
													onClick={() => onSubmitNewStaffGrade(setSubmitting, setFieldValue)}
												>
													Add
												</Button>
											</Col>
										</Row>
									)}
									{values.staffGradeHistory
										?.sort((a, b) => (a.effectiveDate > b.effectiveDate ? -1 : 1))
										.map((staffGrade, index) => (
											<Row key={index} className="mt-3">
												<Col sm={4}>{staffGrade.grade.name}</Col>
												<Col sm={4}>{formatDateExcludeEpoch(staffGrade.effectiveDate)}</Col>
												<Col />
											</Row>
										))}
								</Col>
								<Col className="p-2" sm="auto" />
								<Col>
									<Row>
										<Col sm="auto">
											<Form.Label>Standard Working Hours </Form.Label>
										</Col>
									</Row>
									<Row className="mini-table-header align-items-center py-2">
										<Col sm={4}>Hours</Col>
										<Col sm={4}>Effective Date</Col>
										<Col sm={4} className="center-flex">
											<Button
												variant="secondary"
												className="px-2 py-1"
												disabled={isSubmitting || addingStaffGrade || addingStaffHours}
												onClick={() => {
													setNewStaffHours(defaultStaffStandardWorkingHours)
													setAddingStaffHours(true)
												}}
											>
												Add
											</Button>
										</Col>
									</Row>
									{addingStaffHours && (
										<Row className="align-items-center mt-3">
											<Col sm={4}>
												<FormNumber
													name="newStaffHoursHours"
													value={newStaffHours.hours || 0}
													onChange={(value) => setNewStaffHours({ ...newStaffHours, hours: value })}
													onFocus={(e) => e.target.select()}
													min={0}
													max={6000}
													required
												/>
											</Col>
											<Col sm={4}>
												<FormDate
													name="newStaffHoursEffectiveDate"
													value={newStaffHours.effectiveDate}
													onChange={(e) => setNewStaffHours({ ...newStaffHours, effectiveDate: e.target.value })}
													min={DateTime.now().plus({ days: 1 }).toISODate()}
												/>
											</Col>
											<Col sm={2} className="center-flex">
												<Button
													variant="secondary"
													className="px-2 py-1"
													disabled={isSubmitting}
													onClick={() => setAddingStaffHours(false)}
												>
													Cancel
												</Button>
											</Col>
											<Col sm={2} className="center-flex">
												<Button
													variant="primary"
													className="px-2 py-1"
													disabled={isSubmitting}
													onClick={() => onSubmitNewStaffHours(setSubmitting, setFieldValue)}
												>
													Add
												</Button>
											</Col>
										</Row>
									)}
									{values.standardWorkingHoursHistory
										?.sort((a, b) => (a.effectiveDate > b.effectiveDate ? -1 : 1))
										.map((swh, index) => (
											<Row key={index} className="mt-3">
												<Col sm={4}>{swh.hours}</Col>
												<Col sm={4}>{formatDateExcludeEpoch(swh.effectiveDate)}</Col>
												<Col />
											</Row>
										))}
								</Col>
							</Row>
						)}
					</Modal.Body>
					<Modal.Footer>
						<Row className="flex-grow-1 w-100 align-items-center">
							<Col sm="auto">
								<Button
									variant="secondary"
									className="p-2"
									disabled={isSubmitting || addingStaffGrade || addingStaffHours}
									onClick={() => {
										props.setOffboardStaff(props.modalStaff)
										onHide(isSubmitting)
									}}
								>
									Offboard
								</Button>
							</Col>
							<Col className="d-flex justify-content-end">
								<Button
									variant="secondary"
									className="p-2 me-2"
									disabled={isSubmitting || addingStaffGrade || addingStaffHours}
									onClick={() => onHide(isSubmitting)}
								>
									Cancel
								</Button>

								{values.permissionID && values.user === null ? (
									<>
										<Button
											variant="primary"
											className="px-3 py-2 me-2"
											disabled={isSubmitting || addingStaffGrade || addingStaffHours}
											onClick={() => {
												setValues({ ...values, sendInvite: false })
												handleSubmit()
											}}
										>
											Save
										</Button>
										<Button
											variant="secondary"
											className="px-3 py-2"
											disabled={isSubmitting || addingStaffGrade || addingStaffHours}
											onClick={() => {
												setValues({ ...values, sendInvite: true })
												handleSubmit()
											}}
										>
											Save and Invite
										</Button>
									</>
								) : (
									<Button
										variant="primary"
										className="px-3 py-2"
										disabled={isSubmitting || addingStaffGrade || addingStaffHours}
										onClick={() => {
											setValues({ ...values, sendInvite: false })
											handleSubmit()
										}}
									>
										Save
									</Button>
								)}
							</Col>
						</Row>
					</Modal.Footer>
				</Modal>
			)}
		</Formik>
	)
}

export default EditStaffModal
